import type { FetchResult, Operation } from "@apollo/client";
import { ApolloLink, Observable } from "@apollo/client";
import type { DocumentNode } from "graphql";

import type { LoadingData } from "@shared/context/LoadingContext";
import { AnalyticsLogEvent_Shared_Mutation } from "@shared/data/mutations/AnalyticsLogEventMutation/AnalyticsLogEvent.mutation";

import { getOperationName } from "./getOperationName/getOperationName";
import { getOperationType } from "./getOperationType/getOperationType";

interface Params
  extends Pick<LoadingData, "incrementMutationLoading" | "decrementMutationLoading"> {
  ignoreMutationList?: DocumentNode[];
}

const defaultIgnoreMutationList = [AnalyticsLogEvent_Shared_Mutation];
const isMutation = (query: DocumentNode) => {
  return getOperationType(query) === "mutation";
};

const isInIgnoreList = (ignoreMutationList: DocumentNode[] = [], mutationName: string) => {
  return !!ignoreMutationList.find(ignoredMutation => {
    const ignoredMutationName = getOperationName(ignoredMutation);

    return ignoredMutationName === mutationName;
  });
};

const skipMutationLoadingLink = (
  operation: Operation,
  ignoreMutationList: Params["ignoreMutationList"]
) => {
  const ignoreMutationListWithDefaults = [
    ...defaultIgnoreMutationList,
    ...(ignoreMutationList || []),
  ];

  return (
    !isMutation(operation.query) ||
    isInIgnoreList(ignoreMutationListWithDefaults, operation.operationName)
  );
};

const getMutationLoadingLink = ({
  incrementMutationLoading,
  decrementMutationLoading,
  ignoreMutationList,
}: Params) =>
  new ApolloLink((operation, forward): Observable<FetchResult> => {
    if (skipMutationLoadingLink(operation, ignoreMutationList)) {
      return forward(operation);
    }

    incrementMutationLoading();
    const opt = forward(operation);

    return new Observable<FetchResult>(observer => {
      const sub = opt.subscribe({
        next: observer.next.bind(observer),
        error: error => {
          decrementMutationLoading();
          observer.error(error);
        },
        complete: () => {
          decrementMutationLoading();
          observer.complete();
        },
      });

      return () => sub.unsubscribe();
    });
  });

export { getMutationLoadingLink };
